// src/student/pages/StudentDailyCA.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  FileText,
  Search,
  Calendar,
  Download,
  Filter,
  RefreshCw,
} from "lucide-react";
import { studentPortalAPI } from "../services/studentApi";

const CATEGORIES = [
  "All",
  "National",
  "International",
  "Economy",
  "Environment",
  "Science & Tech",
  "Polity & Governance",
];

export default function StudentDailyCA() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchDate, setSearchDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeItem, setActiveItem] = useState(null);

  const fetchDailyCA = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await studentPortalAPI.getDailyCA();
      if (res.success && Array.isArray(res.data)) {
        setItems(res.data);
      }
    } catch (err) {
      setError(err.message || "Failed to load Daily Current Affairs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDailyCA();
  }, [fetchDailyCA]);

  const resolveUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const base = (
      process.env.REACT_APP_API_URL || "http://localhost:5000/api"
    ).replace(/\/api$/, "");
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  // Filter items
  const filteredItems = items.filter((item) => {
    const matchesCat =
      selectedCategory === "All" ||
      (item.category || "").toLowerCase() === selectedCategory.toLowerCase();

    const itemDate = item.date
      ? new Date(item.date).toISOString().split("T")[0]
      : "";
    const matchesDate = !searchDate || itemDate === searchDate;

    const matchesQuery =
      !searchQuery ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description &&
        item.description.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesCat && matchesDate && matchesQuery;
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-red-950 via-red-900 to-[#0b1528] rounded-2xl p-6 text-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-white/10 px-2.5 py-0.5 rounded-full">
              Free UPSC Initiative
            </span>
            <span className="text-xs text-slate-300 font-medium">Daily Briefs</span>
          </div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight">
            Daily Current Affairs
          </h1>
          <p className="text-xs text-slate-300 mt-1 max-w-xl">
            Curated daily editorials, PIB releases, and national/international news summaries prepared for UPSC CSE Prelims & Mains.
          </p>
        </div>

        <button
          onClick={fetchDailyCA}
          className="self-start md:self-auto flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold backdrop-blur-xs transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span>Refresh Feed</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-2xs space-y-3">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Keyword Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by keyword, topic or ministry..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 focus:bg-white"
            />
          </div>

          {/* Date Picker Filter */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 text-slate-400" size={15} />
              <input
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                className="pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
              />
            </div>

            {searchDate && (
              <button
                onClick={() => setSearchDate("")}
                className="text-xs text-slate-500 hover:text-slate-800 font-medium px-2 py-1 bg-slate-100 rounded"
              >
                Clear Date
              </button>
            )}
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
          <span className="text-[11px] font-semibold text-slate-400 flex items-center gap-1 mr-1 flex-shrink-0">
            <Filter size={12} /> Category:
          </span>
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1 rounded-full font-medium transition-colors flex-shrink-0 ${
                selectedCategory === cat
                  ? "bg-red-900 text-white shadow-2xs"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid & Preview */}
      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">
          Loading Daily Current Affairs...
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="py-16 bg-white rounded-2xl border border-slate-200 text-center">
          <FileText className="mx-auto text-slate-300 mb-2" size={36} />
          <p className="text-sm font-semibold text-slate-700">No current affairs found</p>
          <p className="text-xs text-slate-400 mt-1">Try selecting a different date or category filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => {
            const pdfLink = resolveUrl(item.pdfUrl || item.fileUrl);
            return (
              <div
                key={item._id}
                className="bg-white rounded-xl p-5 border border-slate-200 hover:border-red-800/40 hover:shadow-md transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-red-800 bg-red-50 px-2.5 py-0.5 rounded-full border border-red-100">
                      {item.category || "General"}
                    </span>
                    <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1">
                      <Calendar size={12} />
                      {item.date ? new Date(item.date).toLocaleDateString("en-IN") : "Today"}
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 line-clamp-2 mb-2 hover:text-red-900 transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-xs text-slate-600 line-clamp-3 leading-relaxed">
                    {item.description || "No detailed summary provided for this briefing."}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <button
                    onClick={() => setActiveItem(item)}
                    className="text-xs font-semibold text-slate-700 hover:text-red-900 transition-colors"
                  >
                    Read Summary
                  </button>

                  {pdfLink ? (
                    <a
                      href={pdfLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-900 text-xs font-semibold transition-colors"
                    >
                      <Download size={13} />
                      <span>PDF</span>
                    </a>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-medium">Text Only</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reader Modal */}
      {activeItem && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-2xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-800 bg-red-50 px-2 py-0.5 rounded">
                  {activeItem.category || "General"}
                </span>
                <span className="text-xs text-slate-400">
                  {activeItem.date ? new Date(activeItem.date).toLocaleDateString("en-IN") : ""}
                </span>
              </div>
              <button
                onClick={() => setActiveItem(null)}
                className="text-slate-400 hover:text-slate-700 text-sm font-bold px-2 py-1"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              <h2 className="text-base font-bold text-slate-900">
                {activeItem.title}
              </h2>
              <div className="text-xs text-slate-700 whitespace-pre-line leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">
                {activeItem.description}
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
              {resolveUrl(activeItem.pdfUrl || activeItem.fileUrl) ? (
                <a
                  href={resolveUrl(activeItem.pdfUrl || activeItem.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-4 py-2 bg-red-900 text-white rounded-xl text-xs font-semibold hover:bg-red-800 transition-colors shadow-xs"
                >
                  <Download size={14} />
                  <span>Download Full Document PDF</span>
                </a>
              ) : (
                <span className="text-xs text-slate-400">No PDF attached</span>
              )}
              <button
                onClick={() => setActiveItem(null)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-xs font-semibold hover:bg-slate-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
